(() => {
  // Links work immediately. Fetch only decorative imagery, once per department,
  // on intent/open; keep it across page visits without caching prices or stock.
  const imageRequests = new WeakMap();
  const imageCachePrefix = `hmg-menu-images-v52-aligned-20261102-r3:${window.Shopify?.theme?.id || ''}:`;
  const loadMenuImages = (grid) => {
    if (!grid || grid.dataset.imagesLoaded || imageRequests.has(grid)) return;
    const source = grid.dataset.hmgImagesUrl;
    if (!source) return;
    const url = new URL(source, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/collections/')) return;
    url.hash = '';
    url.search = '';
    url.searchParams.set('section_id', 'hmg-menu-images');
    const cacheKey = imageCachePrefix + url.pathname;
    const applyImages = (markup) => {
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const response = doc.querySelector('[data-hmg-menu-images]');
      if (!response) throw new Error('Menu images unavailable');
      const images = new Map([...response.querySelectorAll('template[data-hmg-menu-image]')]
        .map((node) => [node.dataset.hmgMenuImage, node.content.querySelector('img')]));
      grid.querySelectorAll('[data-hmg-menu-image]').forEach((slot) => {
        const img = images.get(slot.dataset.hmgMenuImage);
        if (img) { img.decoding = 'async'; slot.hidden = false; slot.replaceChildren(img.cloneNode(true)); }
        else slot.hidden = true;
      });
      grid.dataset.imagesLoaded = 'true';
    };
    const request = (async () => {
      try {
        try {
          const cached = JSON.parse(sessionStorage.getItem(cacheKey));
          if (cached && Date.now() - cached.time < 1800000) {
            applyImages(cached.markup);
            return;
          }
        } catch (_) { /* Storage may be disabled; network loading still works. */ }
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Menu images unavailable');
        const markup = await response.text();
        applyImages(markup);
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), markup })); } catch (_) { /* Optional cache. */ }
      } catch (_) {
        // Decoration must never block the links, keyboard controls or scrolling.
        grid.querySelectorAll('[data-hmg-menu-image]').forEach((slot) => { slot.hidden = true; });
      } finally { imageRequests.delete(grid); }
    })();
    imageRequests.set(grid, request);
  };
  const isDesktop = () => window.innerWidth >= 990;
  const availableSpace = (bottom) => Math.max(0, (window.visualViewport?.height || window.innerHeight) + (window.visualViewport?.offsetTop || 0) - bottom - 16);
  const fitNavigation = () => {
    document.querySelectorAll('.hmg-native-nav').forEach((nav) => {
      const bottom = nav.getBoundingClientRect().bottom;
      const space = `${availableSpace(bottom)}px`;
      const top = `${bottom}px`;
      const header = nav.closest('.hmg-native-header');
      if (nav.style.getPropertyValue('--hmg-menu-space') !== space) nav.style.setProperty('--hmg-menu-space', space);
      if (header.style.getPropertyValue('--hmg-overlay-top') !== top) header.style.setProperty('--hmg-overlay-top', top);
    });
  };
  let resizeFrame;
  const scheduleFit = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => { resizeFrame = null; fitNavigation(); });
  };
  window.addEventListener('resize', scheduleFit, { passive: true });
  window.addEventListener('scroll', () => { if (document.querySelector('.hmg-nav-disclosure[open]')) scheduleFit(); }, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleFit, { passive: true });
  const initialiseNavigation = (scope = document) => {
    scope.querySelectorAll('.hmg-native-nav').forEach((nav) => {
      if (nav.dataset.enhanced) return;
      nav.dataset.enhanced = 'true';
      const disclosures = [...nav.querySelectorAll('.hmg-nav-disclosure')];
      const prepareImages = (details) => {
        const index = details.dataset.hmgDepartmentIndex;
        const grid = details.querySelector('.hmg-featured-groups .hmg-mega-grid') ||
          (index !== undefined ? nav.querySelector(`.hmg-department-disclosure[data-hmg-department-index="${index}"] .hmg-mega-grid`) :
            details.querySelector('.hmg-department-disclosure[open] .hmg-mega-grid') || details.querySelector('.hmg-mega-grid'));
        loadMenuImages(grid);
      };
      const restoreGroups = (details) => {
        const host = details.querySelector('.hmg-featured-groups');
        const grid = host?.querySelector('.hmg-mega-grid');
        if (!grid) return;
        const original = nav.querySelector(`.hmg-department-disclosure[data-hmg-department-index="${details.dataset.hmgDepartmentIndex}"] .hmg-department-links`);
        original?.append(grid);
      };

      disclosures.forEach((details) => {
        let openTimer;
        let closeTimer;
        const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches && isDesktop();
        details.querySelector('summary').addEventListener('pointerenter', () => {
          if (!finePointer()) return;
          clearTimeout(closeTimer);
          openTimer = setTimeout(() => { prepareImages(details); details.open = true; }, 350);
        });
        details.querySelector('summary').addEventListener('focus', () => prepareImages(details));
        details.addEventListener('pointerenter', () => clearTimeout(closeTimer));
        details.addEventListener('pointerleave', () => {
          clearTimeout(openTimer);
          if (finePointer() && !details.contains(document.activeElement)) closeTimer = setTimeout(() => { details.open = false; }, 400);
        });
        details.querySelector('summary').addEventListener('click', () => { clearTimeout(openTimer); clearTimeout(closeTimer); });
        details.addEventListener('toggle', () => {
          if (details.open) {
            disclosures.forEach((other) => { if (other !== details) { restoreGroups(other); other.open = false; } });
            const host = details.querySelector('.hmg-featured-groups');
            if (host) {
              const grid = nav.querySelector(`.hmg-department-disclosure[data-hmg-department-index="${details.dataset.hmgDepartmentIndex}"] .hmg-mega-grid`);
              if (grid) host.append(grid);
            }
            if (isDesktop() && details.classList.contains('hmg-nav-all') && !details.querySelector('.hmg-department-disclosure[open]')) {
              const first = details.querySelector('.hmg-department-disclosure');
              if (first) first.open = true;
            }
            prepareImages(details);
            scheduleFit();
          } else { restoreGroups(details); }
        });
      });
      const departments = [...nav.querySelectorAll('.hmg-department-disclosure')];
      departments.forEach((department) => {
        const back = department.querySelector('[data-hmg-department-back]');
        if (back) {
          back.hidden = false;
          back.addEventListener('click', () => {
            department.open = false;
            department.querySelector('summary').focus();
          });
        }
        department.addEventListener('toggle', () => {
          if (department.open) {
            departments.forEach((other) => { if (other !== department) other.open = false; });
            department.querySelector('.hmg-department-links').scrollTop = 0;
            loadMenuImages(department.querySelector('.hmg-mega-grid'));
          }
        });
        department.querySelector('summary').addEventListener('click', (event) => {
          if (isDesktop() && department.open) event.preventDefault();
        });
      });
      nav.querySelectorAll('[data-hmg-menu-close]').forEach((button) => {
        button.addEventListener('click', () => {
          const disclosure = button.closest('.hmg-nav-disclosure');
          disclosure.open = false;
          disclosure.querySelector('summary').focus();
        });
      });
      nav.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = event.target.closest('.hmg-nav-disclosure[open]');
        if (open) { open.open = false; open.querySelector('summary').focus(); event.preventDefault(); }
      });
      nav.addEventListener('focusout', () => {
        requestAnimationFrame(() => { if (!nav.contains(document.activeElement)) disclosures.forEach((d) => { d.open = false; }); });
      });
    });
  };
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.hmg-nav-disclosure[open]').forEach((d) => { if (!d.contains(event.target)) d.open = false; });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.hmg-nav-disclosure[open]').forEach((disclosure) => {
      if (!disclosure.contains(event.target)) disclosure.open = false;
    });
  });
  document.addEventListener('hmg:load-menu-images', (event) => loadMenuImages(event.detail?.grid));
  initialiseNavigation();
  scheduleFit();
  document.addEventListener('shopify:section:load', (event) => initialiseNavigation(event.target));

  class HmgSiteSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.hmg-search-suggestions');
      this.status = this.querySelector('[data-hmg-search-status]');
      this.cache = new Map();
      this.activeIndex = -1;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.close();
        if (this.input.value.trim().length < 2) { this.status.textContent = ''; return; }
        this.timer = setTimeout(() => this.search(), 220);
      });
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length >= 2) this.search(); });
      this.input.addEventListener('keydown', (event) => this.onKey(event));
      this.addEventListener('focusout', () => requestAnimationFrame(() => { if (!this.contains(document.activeElement)) this.close(); }));
      this.querySelector('form').addEventListener('submit', (event) => { if (!this.input.value.trim()) event.preventDefault(); });
      this.outside = (event) => { if (!this.contains(event.target)) this.close(); };
      document.addEventListener('click', this.outside);
    }
    disconnectedCallback() {
      clearTimeout(this.timer); this.controller?.abort();
      document.removeEventListener('click', this.outside);
      this.ready = false;
    }
    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) return;
      if (this.cache.has(term)) { this.render(this.cache.get(term), term); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({ q: term, 'resources[type]': 'product,collection', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last', section_id: 'hmg-search-suggestions' });
      this.status.textContent = this.dataset.loading;
      try {
        const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-hmg-results-count]');
        if (!node) throw new Error('Search response unavailable');
        const markup = node.outerHTML;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, markup);
        this.render(markup, term);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.close(); this.status.textContent = '';
      }
    }
    render(markup, term) {
      if (term !== this.input.value.trim() || !this.contains(document.activeElement)) return;
      const space = availableSpace(this.getBoundingClientRect().bottom + 8);
      this.results.innerHTML = markup;
      this.style.setProperty('--hmg-search-space', `${space}px`);
      this.results.querySelector('[role="listbox"]').id = this.input.getAttribute('aria-controls');
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.status.textContent = this.results.firstElementChild.dataset.hmgResultsLabel;
    }
    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }
    onKey(event) {
      if (event.key === 'Escape') { clearTimeout(this.timer); this.close(); this.controller?.abort(); this.status.textContent = ''; return; }
      if (this.results.hidden) return;
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!options.length) return;
        this.activeIndex = this.activeIndex < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
        const selected = options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', selected.id);
        selected.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault(); options[this.activeIndex]?.querySelector('a').click();
      }
    }
  }
  if (!customElements.get('hmg-site-search')) customElements.define('hmg-site-search', HmgSiteSearch);
})();
